import { useEffect, useRef } from "react";
import { toast } from "sonner";

const DURATION_MS = 6000;

const plural = (n, one, many) => (n === 1 ? one : many.replace("{n}", n));

/**
 * Announces what other visitors are doing, as a toast that fades on its own.
 *
 * Only ever fires on an *increase*. The page polls every fifteen seconds, so
 * reacting to the value rather than the change would re-announce the same two
 * people for as long as they stayed on the page.
 *
 * One toast at a time, strongest signal first: someone asking for a quote says
 * more than someone adding to a cart, which says more than someone reading.
 * Three at once would be a wall of notifications about a single product.
 */
export const usePresenceToasts = ({ viewers = 0, cart_adds: cartAdds = 0, orders = 0 }) => {
  const previous = useRef({ viewers: 0, cartAdds: 0, orders: 0 });

  useEffect(() => {
    const before = previous.current;
    previous.current = { viewers, cartAdds, orders };

    const messages = [];
    if (orders > before.orders) {
      messages.push(plural(orders - before.orders,
        "Alguém pediu orçamento deste produto",
        "{n} pessoas pediram orçamento deste produto"));
    }
    if (cartAdds > before.cartAdds) {
      messages.push(plural(cartAdds - before.cartAdds,
        "Alguém adicionou este produto ao orçamento",
        "{n} pessoas adicionaram este produto ao orçamento"));
    }
    if (viewers > before.viewers) {
      messages.push(plural(viewers,
        "Mais alguém está a ver este produto agora",
        "Mais {n} pessoas estão a ver este produto agora"));
    }

    if (messages.length) {
      // Top right: the bottom right is occupied by the floating WhatsApp
      // button, which a toast landing there covers up.
      toast(messages[0], { duration: DURATION_MS, position: "top-right" });
    }
  }, [viewers, cartAdds, orders]);
};
